import { type PropsWithChildren } from 'react'
import TextButton, { VARIANTS } from 'mixtiles-web-common/ui/TextButton'
import { ReactComponent as ErrorIcon } from './icons/error.svg'
import { ReactComponent as ReloadIcon } from './icons/reload.svg'
import useOnMount from '../../hooks/useOnMount'
import { EVENT_NAMES, analytics } from '../../services/Analytics/Analytics'
import { ErrorBoundary } from 'react-error-boundary'
import { ThemeProvider } from 'styled-components'
import { photosTheme } from 'mixtiles-web-common'
import { LANGUAGES } from 'locale/Translation.consts'
import * as S from './AppErrorFallback.styles'
import { logger } from '../../services/logger'
import { extractErrorMessage } from '../../utils/ApiUtils'
import { intercomService } from '../../services/Analytics/platforms/intercom'

// Error fallbacks render outside DefaultProviders, which is where `theme.language`
// is normally injected; the fallback copy is English-only anyway.
export const errorFallbackTheme = {
  ...photosTheme,
  language: LANGUAGES.ENGLISH,
}

const STALE_CHUNK_RELOAD_KEY = 'appErrorFallback:moduleReload'

function shouldReloadForStaleChunk(): boolean {
  return !sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)
}

function markStaleChunkReload() {
  sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1')
}

export function AppErrorFallbackWrapper({
  children,
  themeColor,
  ...props
}: PropsWithChildren<{ themeColor?: string }>) {
  const onReset = () => {
    window.location.reload()
  }
  return (
    <ErrorBoundary
      onReset={onReset}
      fallbackRender={({ error, resetErrorBoundary }) => (
        // Self-theme: this boundary now lives above the app's StyledThemeProvider.
        <ThemeProvider theme={errorFallbackTheme}>
          <AppErrorFallback
            themeColor={themeColor}
            {...props}
            error={error}
            resetErrorBoundary={resetErrorBoundary}
          />
        </ThemeProvider>
      )}
    >
      {children}
    </ErrorBoundary>
  )
}

export function AppErrorFallbackView({
  themeColor = photosTheme.colors.primary,
  onTryAgain,
  onTalkToUs,
}: {
  themeColor?: string
  onTryAgain: () => void
  onTalkToUs?: () => void
}) {
  return (
    <S.Container data-testid="app-error-fallback">
      <div>
        <ErrorIcon fill={themeColor} />
      </div>
      <S.Title>Something went wrong</S.Title>
      <S.Description>
        Oops! There was a problem loading our website.
        <br />
        Please try again in a few minutes.
      </S.Description>
      <br />
      <S.TryAgainButton
        data-testid="error-try-again-button"
        onClick={onTryAgain}
        style={{ backgroundColor: themeColor }}
      >
        <ReloadIcon />
        <S.TryAgainText>Try Again</S.TryAgainText>
      </S.TryAgainButton>
      {onTalkToUs && (
        <S.TalkToUsContainer>
          <TextButton variant={VARIANTS.underline} onClick={onTalkToUs}>
            Talk to Us
          </TextButton>
        </S.TalkToUsContainer>
      )}
    </S.Container>
  )
}

export function AppErrorFallback({
  themeColor = photosTheme.colors.primary,
  error,
  resetErrorBoundary,
}: {
  themeColor?: string
  error: Error
  resetErrorBoundary: () => void
}) {
  useOnMount(() => {
    const errorMessage = extractErrorMessage(error)
    const isModuleError =
      errorMessage?.includes('Importing a module script failed') ||
      errorMessage?.includes('Failed to fetch dynamically imported module')

    if (isModuleError && shouldReloadForStaleChunk()) {
      markStaleChunkReload()
      window.location.reload()
      return
    }

    // Extract module name from stack trace if it's a module error
    let failedModule = null
    if (isModuleError && error?.stack) {
      // Try to extract module name from common patterns
      const moduleMatch =
        error.stack.match(/import\(['"`]([^'"`]+)['"`]\)/) ||
        error.stack.match(/lazy\(\(\) => import\(['"`]([^'"`]+)['"`]\)/) ||
        error.stack.match(/([^/]+)\.chunk\.js/) ||
        error.stack.match(/at ([A-Z][a-zA-Z]+)\./)

      if (moduleMatch) {
        failedModule =
          moduleMatch[1]?.replace(/\.(js|jsx|ts|tsx)$/, '') || moduleMatch[1]
      }
    }

    analytics.track('App Error Occurred', {
      error: errorMessage,
      stacktrace: error?.stack,
      path: window.location.pathname,
      isModuleError,
      failedModule,
      isHydrated:
        document.getElementById('root')?.getAttribute('data-hydrated') ===
        'true',
    })
    logError(error)
  })

  const onTryAgainPressed = () => {
    analytics.track('Try Again Tapped', { screen: 'AppErrorFallback' })
    sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY)
    resetErrorBoundary()
  }

  const onTalkToUsPressed = () => {
    analytics.track(EVENT_NAMES.TALK_TO_US_TAPPED, {
      screen: 'AppErrorFallback',
    })
    intercomService.show()
  }

  /**
   * Write error to log in order to be monitored
   */
  const logError = (error: Error) => {
    // most errors reaching here will not be reported to sentry from this call but before that
    // because sentry manages to capture the errors before the ErrorBoundary does and marks them
    // reported https://github.com/getsentry/sentry-javascript/pull/4067
    logger.error('App Error Occurred - Fallback Loaded', error, {
      tags: {
        isFallback: true,
      },
    })
  }

  return (
    <AppErrorFallbackView
      themeColor={themeColor}
      onTryAgain={onTryAgainPressed}
      onTalkToUs={onTalkToUsPressed}
    />
  )
}
